import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Input, Select, Checkbox, List, Popconfirm, Tag, Space, Divider, message, Spin } from "antd";
import { apiError, teacherName } from "./utils";

const axios = require("axios").default;

/**
 * Manager'lar yalnızca isimdir (hesap/parola yok). "Eğitmen Ata" ile mevcut eğitmenler
 * seçilip manager'a atanır; seçimi kaldırılanlar "Atanmamış" olur.
 */
const ManagerModal = ({ open, onClose, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [managers, setManagers] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null); // { id, name }
  const [assignId, setAssignId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("payroll/managers");
      setManagers(data.list);
      setTrainers(data.trainers);
      return data;
    } catch (err) {
      message.error(apiError(err, "Manager listesi alınamadı."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const chooseManager = (id, list = managers) => {
    setAssignId(id);
    const m = list.find((x) => x.id === id);
    setSelected(m ? m.trainer_ids : []);
  };

  const changed = async (keepAssign = assignId) => {
    const data = await load();
    if (data && keepAssign) chooseManager(keepAssign, data.list);
    onChanged();
  };

  const addManager = async () => {
    try {
      await axios.post("payroll/managers", { name: newName });
      setNewName("");
      message.success("Manager eklendi.");
      changed();
    } catch (err) {
      message.error(apiError(err));
    }
  };

  const renameManager = async () => {
    try {
      await axios.put(`payroll/managers/${editing.id}`, { name: editing.name });
      setEditing(null);
      changed();
    } catch (err) {
      message.error(apiError(err));
    }
  };

  const removeManager = async (id) => {
    try {
      await axios.delete(`payroll/managers/${id}`);
      message.success("Manager silindi; eğitmenleri Atanmamış durumuna geçti.");
      if (assignId === id) {
        setAssignId(null);
        setSelected([]);
      }
      changed(assignId === id ? null : assignId);
    } catch (err) {
      message.error(apiError(err));
    }
  };

  const saveAssignments = async () => {
    setSaving(true);
    try {
      await axios.post(`payroll/managers/${assignId}/assign`, { trainer_ids: selected });
      message.success("Eğitmen atamaları kaydedildi.");
      changed();
    } catch (err) {
      message.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const managerNames = useMemo(() => Object.fromEntries(managers.map((m) => [m.id, m.name])), [managers]);
  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR");
    return trainers.filter(
      (t) => !q || `${teacherName(t)} ${t.email || ""}`.toLocaleLowerCase("tr-TR").includes(q)
    );
  }, [trainers, search]);

  return (
    <Modal title="Manager Yönetimi" visible={open} onCancel={onClose} footer={<Button onClick={onClose}>Kapat</Button>} width={640}>
      <Spin spinning={loading}>
        <p style={{ color: "#666", marginTop: 0 }}>
          Manager isimlerini ekleyin ve mevcut eğitmenleri atayın. Manager için hesap veya şifre gerekmez.
        </p>
        <Space.Compact style={{ width: "100%" }}>
          <Input
            placeholder="Örn. Maria Santos"
            maxLength={90}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onPressEnter={addManager}
          />
          <Button type="primary" disabled={!newName.trim()} onClick={addManager}>＋ Manager Ekle</Button>
        </Space.Compact>

        <List
          style={{ marginTop: 12 }}
          size="small"
          bordered
          locale={{ emptyText: "Henüz manager yok" }}
          dataSource={managers}
          renderItem={(m) => (
            <List.Item
              actions={[
                editing?.id === m.id ? (
                  <Button key="save" size="small" type="link" onClick={renameManager}>Kaydet</Button>
                ) : (
                  <Button key="edit" size="small" type="link" onClick={() => setEditing({ id: m.id, name: m.name })}>Düzenle</Button>
                ),
                <Popconfirm
                  key="del"
                  title="Manager silinsin mi? Eğitmenleri Atanmamış olur."
                  okText="Evet"
                  cancelText="Hayır"
                  onConfirm={() => removeManager(m.id)}
                >
                  <Button size="small" type="link" danger>Sil</Button>
                </Popconfirm>,
              ]}
            >
              {editing?.id === m.id ? (
                <Input
                  size="small"
                  maxLength={90}
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  onPressEnter={renameManager}
                />
              ) : (
                <span>
                  <b>{m.name}</b> <span style={{ color: "#888" }}>· {m.trainer_count} eğitmen</span>
                </span>
              )}
            </List.Item>
          )}
        />

        <Divider orientation="left" plain>Manager'a Eğitmen Ata</Divider>
        <Select
          style={{ width: "100%" }}
          placeholder="Manager seç"
          value={assignId}
          onChange={(id) => chooseManager(id)}
          options={managers.map((m) => ({ value: m.id, label: m.name }))}
        />
        {assignId && (
          <>
            <Input
              style={{ marginTop: 10 }}
              placeholder="Eğitmen ara..."
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div style={{ maxHeight: 280, overflowY: "auto", marginTop: 8, border: "1px solid #f0f0f0", borderRadius: 6, padding: "4px 10px" }}>
              <Checkbox.Group value={selected} onChange={setSelected} style={{ width: "100%" }}>
                {filtered.map((t) => (
                  <div key={t.id} style={{ padding: "5px 0" }}>
                    <Checkbox value={t.id}>
                      {teacherName(t)}
                      <span style={{ color: "#999", fontSize: 12 }}> · {t.email}</span>
                      {Number(t.status) !== 1 && <Tag style={{ marginLeft: 6 }}>Pasif</Tag>}
                      {t.manager_id && t.manager_id !== assignId && (
                        <Tag color="orange" style={{ marginLeft: 6 }}>{managerNames[t.manager_id]}</Tag>
                      )}
                    </Checkbox>
                  </div>
                ))}
              </Checkbox.Group>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10 }}>
              <span style={{ color: "#888", fontSize: 12 }}>
                {selected.length} eğitmen seçili · Başka manager'daki eğitmen seçilirse bu manager'a taşınır.
              </span>
              <Button type="primary" loading={saving} onClick={saveAssignments}>Seçilenleri Ata</Button>
            </div>
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default ManagerModal;
